import { useState, useEffect, useCallback, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import {
  getOfferings,
  purchasePackage,
  restorePurchases as rcRestore,
  presentPaywall,
  presentPaywallIfNeeded,
  checkEntitlement,
  ENTITLEMENT_ID,
  getPurchasesErrorCode,
  identifyUser,
  getCustomerInfo,
} from '../services/revenuecat';
import { usePremium } from './usePremium';
import { syncIapWithBackend } from '../services/iap';

interface Package {
  identifier: string;
  packageType?: string;
  offeringIdentifier: string;
  product: {
    identifier?: string;
    priceString: string;
    title: string;
    subscriptionPeriod?: string | null;
  };
}

interface Offerings {
  current?: {
    monthly?: Package;
    annual?: Package;
    yearly?: Package;
    availablePackages: Package[];
  } | null;
}

const packageSearchText = (pkg: Package): string => [
  pkg.identifier,
  pkg.packageType,
  pkg.product.identifier,
  pkg.product.title,
  pkg.product.subscriptionPeriod,
].filter(Boolean).join(' ').toLowerCase();

const findMonthlyPackage = (current: Offerings['current']): Package | undefined | null => {
  if (!current) return null;
  return current.availablePackages.find((pkg) => {
    const text = packageSearchText(pkg);
    return pkg.packageType === 'MONTHLY' || pkg.product.subscriptionPeriod === 'P1M' || text.includes('monthly');
  }) ?? current.monthly;
};

const findAnnualPackage = (current: Offerings['current']): Package | undefined | null => {
  if (!current) return null;
  return current.availablePackages.find((pkg) => {
    const text = packageSearchText(pkg);
    return (
      pkg.packageType === 'ANNUAL' ||
      pkg.product.subscriptionPeriod === 'P1Y' ||
      text.includes('annual') ||
      text.includes('yearly')
    );
  }) ?? current.annual ?? current.yearly;
};

const hasActiveMembership = (customerInfo: any): boolean => {
  return !!customerInfo?.entitlements?.active?.[ENTITLEMENT_ID];
};

export const useIAPCheckout = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useIAPCheckout must be used within AuthProvider');
  }
  const { user } = context;
  const { refetch: refreshPremium } = usePremium();
  const [offerings, setOfferings] = useState<Offerings | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch offerings on mount
  useEffect(() => {
    let cancelled = false;

    const fetchOfferings = async () => {
      const result = await getOfferings();
      if (!cancelled && result) {
        setOfferings(result as unknown as Offerings);
      }
    };

    fetchOfferings();
    return () => { cancelled = true; };
  }, []);

  /**
   * Purchase a specific package from offerings.
   */
  const purchase = useCallback(async (pkg: Package | undefined | null) => {
    if (!pkg) {
      setError('Package not available. Please try again later.');
      return;
    }

    if (!user) {
      window.dispatchEvent(new CustomEvent('open-user-modal-for-checkout'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await identifyUser(user.id);
      const result = await purchasePackage(pkg);
      if (!result) {
        setError('Membership purchases are unavailable right now. Please close and reopen the app, then try again.');
        return;
      }

      const customerInfo = await getCustomerInfo();
      const entitlementInfo = customerInfo ?? result?.customerInfo;

      // Check if the entitlement is now active
      if (hasActiveMembership(entitlementInfo)) {
        // Sync to backend first so the entitlement refresh below sees the new row.
        await syncIapWithBackend();
        await refreshPremium();
        return;
      }

      setError(
        'Purchase completed, but your membership is not active yet. Please tap Restore Purchases. If this persists, contact support.'
      );
    } catch (err: any) {
      const PURCHASES_ERROR_CODE = getPurchasesErrorCode();
      const errorCode = err?.code;

      // User cancelled — not an error
      if (errorCode === PURCHASES_ERROR_CODE?.PURCHASE_CANCELLED_ERROR) {
        return;
      }

      // Network error — user can retry
      if (errorCode === PURCHASES_ERROR_CODE?.NETWORK_ERROR) {
        setError('Network error. Please check your connection and try again.');
        return;
      }

      // Store problem — may have been charged, allow retry
      if (errorCode === PURCHASES_ERROR_CODE?.STORE_PROBLEM_ERROR) {
        setError('There was a problem with the App Store. Please try again in a moment.');
        return;
      }

      // Already purchased
      if (
        errorCode === PURCHASES_ERROR_CODE?.PRODUCT_ALREADY_PURCHASED_ERROR ||
        errorCode === PURCHASES_ERROR_CODE?.RECEIPT_ALREADY_IN_USE_ERROR
      ) {
        setError('You already have an active subscription. Try "Restore Purchases".');
        return;
      }

      console.error('[IAP] Purchase error:', err);
      setError(err?.message || 'Purchase failed. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user, refreshPremium]);

  const monthlyPackage = findMonthlyPackage(offerings?.current);
  const annualPackage = findAnnualPackage(offerings?.current);

  const purchaseMonthly = useCallback(() => {
    return purchase(monthlyPackage);
  }, [monthlyPackage, purchase]);

  const purchaseAnnual = useCallback(() => {
    return purchase(annualPackage);
  }, [annualPackage, purchase]);

  /**
   * Show the RevenueCat paywall modal.
   * Only presents if user doesn't already have the entitlement.
   */
  const showPaywall = useCallback(async () => {
    if (!user) {
      window.dispatchEvent(new CustomEvent('open-user-modal-for-checkout'));
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const { purchased } = await presentPaywallIfNeeded();
      if (purchased) {
        await syncIapWithBackend();
        await refreshPremium();
      }
      return purchased;
    } catch (err: any) {
      console.error('[IAP] Paywall error:', err);
      setError(err?.message || 'Failed to show paywall');
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, refreshPremium]);

  /**
   * Show the paywall unconditionally (even if user has the entitlement).
   */
  const showPaywallAlways = useCallback(async () => {
    if (!user) {
      window.dispatchEvent(new CustomEvent('open-user-modal-for-checkout'));
      return false;
    }

    setLoading(true);
    setError(null);

    try {
      const { purchased } = await presentPaywall();
      if (purchased) {
        await syncIapWithBackend();
        await refreshPremium();
      }
      return purchased;
    } catch (err: any) {
      console.error('[IAP] Paywall error:', err);
      setError(err?.message || 'Failed to show paywall');
      return false;
    } finally {
      setLoading(false);
    }
  }, [user, refreshPremium]);

  /**
   * Restore previous purchases. Required by App Store guidelines.
   * Should only be triggered by explicit user action (button tap).
   */
  const restorePurchases = useCallback(async () => {
    if (!user) {
      window.dispatchEvent(new CustomEvent('open-user-modal-for-checkout'));
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await identifyUser(user.id);
      const restoredCustomerInfo = await rcRestore();
      const customerInfo = await getCustomerInfo();
      const entitlementInfo = customerInfo ?? restoredCustomerInfo;

      if (hasActiveMembership(entitlementInfo)) {
        await syncIapWithBackend();
        await refreshPremium();
        return;
      }

      setError('No active Wallstreetle membership was found for this Apple ID.');
    } catch (err: any) {
      console.error('[IAP] Restore error:', err);
      setError(err?.message || 'Failed to restore purchases. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [user, refreshPremium]);

  /**
   * Check the entitlement status directly from RevenueCat.
   */
  const hasEntitlement = useCallback(async (): Promise<boolean> => {
    return checkEntitlement();
  }, []);

  return {
    offerings,
    monthlyPackage,
    annualPackage,
    purchaseMonthly,
    purchaseAnnual,
    showPaywall,
    showPaywallAlways,
    restorePurchases,
    hasEntitlement,
    loading,
    error,
  };
};
